/**
 * Centralized Sidebar Styling Configuration
 *
 * All sidebar navigation styles are defined here for consistency
 * and easy global tweaking.
 *
 * Usage:
 * - Import styles into Sidebar component
 * - Modify values here to adjust sidebar appearance globally
 */

// =============================================================================
// NAV ITEM STYLES
// =============================================================================

/**
 * Base classes applied to all nav items (selected and unselected)
 */
export const NAV_ITEM_BASE = [
  'flex',
  'items-center',
  'gap-3',
  'w-full',
  'px-3',
  'py-2',
  'text-sm',
  'rounded-md',
  'transition-colors',
  'text-start',
].join(' ');

/**
 * Selected-state background only — the current module's hue
 * (`--accent-module`) at low alpha. Dark mode needs more alpha to register
 * against the already-dark panel.
 *
 * Exported separately from `NAV_ITEM_SELECTED` because a handful of sidebar
 * rows (workspace/board headers) style their own label and only want the tint.
 * Import one of these two — never re-type the class string, or the next theme
 * change will miss half the sidebar.
 */
export const NAV_ITEM_SELECTED_BG = 'bg-module-accent/15 dark:bg-module-accent/25';

/**
 * Styles for the currently selected/active nav item.
 * The label stays `text-foreground` ink — the tint indicates selection, it
 * doesn't recolor the text.
 */
export const NAV_ITEM_SELECTED = [
  NAV_ITEM_SELECTED_BG,
  'text-foreground',
  'font-medium',
].join(' ');

/**
 * Styles for unselected nav items (hover state)
 */
export const NAV_ITEM_UNSELECTED = 'hover:bg-black/5 dark:hover:bg-white/5';

/**
 * Combined helper function to get nav item classes based on selection state
 */
export function getNavItemClasses(isSelected: boolean): string {
  return `${NAV_ITEM_BASE} ${isSelected ? NAV_ITEM_SELECTED : NAV_ITEM_UNSELECTED}`;
}

// =============================================================================
// NAV ITEM WITH AVATAR (AI agents, etc.)
// =============================================================================

/**
 * Base classes for nav items that include an avatar
 * Slightly more padding for visual balance
 */
export const NAV_ITEM_AVATAR_BASE = [
  'flex',
  'items-center',
  'gap-3',
  'w-full',
  'px-3',
  'py-2.5',
  'text-sm',
  'rounded-md',
  'transition-colors',
  'text-start',
].join(' ');

/**
 * Combined helper for avatar nav items
 */
export function getNavItemAvatarClasses(isSelected: boolean): string {
  return `${NAV_ITEM_AVATAR_BASE} ${isSelected ? NAV_ITEM_SELECTED : NAV_ITEM_UNSELECTED}`;
}

// =============================================================================
// NESTED NAV ITEMS (boards under workspaces, etc.)
// =============================================================================

/**
 * Base classes for nested/child nav items
 * Smaller vertical padding for tighter grouping
 */
export const NAV_ITEM_NESTED_BASE = [
  'flex',
  'items-center',
  'gap-3',
  'w-full',
  'px-3',
  'py-1.5',
  'text-sm',
  'rounded-md',
  'transition-colors',
  'text-start',
].join(' ');

/**
 * Combined helper for nested nav items
 */
export function getNavItemNestedClasses(isSelected: boolean): string {
  return `${NAV_ITEM_NESTED_BASE} ${isSelected ? NAV_ITEM_SELECTED : NAV_ITEM_UNSELECTED}`;
}

// =============================================================================
// COLLAPSED NAV ITEMS (icon-only mode)
// =============================================================================

/**
 * Base classes for collapsed nav items (icon rail mode)
 * Centered icon with square-ish hit area
 */
export const NAV_ITEM_COLLAPSED_BASE = [
  'flex',
  'items-center',
  'justify-center',
  'w-full',
  'h-10',
  'rounded-md',
  'transition-colors',
].join(' ');

/**
 * Combined helper for collapsed nav items
 */
export function getNavItemCollapsedClasses(isSelected: boolean): string {
  return `${NAV_ITEM_COLLAPSED_BASE} ${isSelected ? NAV_ITEM_SELECTED : NAV_ITEM_UNSELECTED}`;
}

// =============================================================================
// SECTION LABELS
// =============================================================================

/**
 * Section label styling (e.g., "Workspaces", "Team Members")
 */
export const NAV_SECTION_LABEL = 'px-3 py-1 text-xs font-semibold text-muted-foreground';
